import { MultipleVideoPlayerDebugState } from "@kalyzee/kast-web-sdk/web-components";
import React from "react";
import { DebugDot, DebugPane, DebugPaneProps } from "./DebugPane";

import styles from "./DebugPane.module.css";

export interface MultiVideoPlayerDebugPaneProps extends Omit<DebugPaneProps, "children"> {
  state: MultipleVideoPlayerDebugState | null;
}

const readyStateLabel = (rs?: number) => {
  switch (rs) {
    case 0: return 'NOTHING';
    case 1: return 'META';
    case 2: return 'CURRENT';
    case 3: return 'FUTURE';
    case 4: return 'ENOUGH';
    default: return '—';
  }
};

export const MultiVideoPlayerDebugPane = ({ state, ...paneProps }: MultiVideoPlayerDebugPaneProps) => {
  if (!state) return null;

  const mounted = state.videos.filter(v => v.mounted);
  const unmountedCount = state.videos.length - mounted.length;

  return (
    <DebugPane {...paneProps}>
      <div className={styles.header}>
        <span>t={((state.currentTime) / 1000).toFixed(1)}s</span>
        <span style={{ color: state.playing ? '#2ecc71' : '#888' }}>
          {state.playing ? 'playing' : 'paused'}
        </span>
        {state.clockPausedForBuffering && (
          <span className={styles.buffering}>BUFFERING</span>
        )}
      </div>
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            <th>source</th>
            <th title="mounted / displayed / active">m/d/a</th>
            <th>readyState</th>
            <th>drift</th>
            <th>preload</th>
          </tr>
        </thead>
        <tbody>
          {mounted.map((v, i) => {
            const name = v.source.name ?? v.source.id ?? `#${i}`;
            const key = v.source.id ?? `${name}-${i}`;
            const driftAbs = v.drift !== undefined ? Math.abs(v.drift) : undefined;
            const driftColor = driftAbs === undefined
              ? '#666'
              : driftAbs > 1500 ? '#e74c3c' : driftAbs > 300 ? '#f39c12' : '#2ecc71';
            const preloadColor = v.bufferedAheadMs === undefined
              ? '#666'
              : v.bufferedAheadMs <= 0 ? '#e74c3c' : v.bufferedAheadMs < 1500 ? '#f39c12' : '#2ecc71';
            return (
              <tr key={key}>
                <td className={styles.sourceName}>{name}</td>
                <td>
                  <div className={styles.dots}>
                    <DebugDot on={v.mounted} title="mounted" />
                    <DebugDot on={v.display} title="display" />
                    <DebugDot on={v.active} title="active" />
                  </div>
                </td>
                <td className={styles.readyState}>{readyStateLabel(v.readyState)}</td>
                <td style={{ color: driftColor }}>
                  {v.drift !== undefined ? `${v.drift > 0 ? '+' : ''}${Math.round(v.drift)}ms` : '—'}
                </td>
                <td style={{ color: preloadColor }}>
                  {v.bufferedAheadMs !== undefined ? `${Math.round(v.bufferedAheadMs)}ms` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {unmountedCount > 0 && (
        <div className={styles.unmounted}>+{unmountedCount} unmounted</div>
      )}
    </DebugPane>
  );
};
